<template>
	<headless-tabs
		v-if="hasVariantCategories"
		:tabs="variantCategories"
		:default-tab="defaultTab"
		@tab-change="handleTabChange"
		panel-style="!mt-4 !mb-4"
	>
		<template v-for="(items, tab) in variantItems" v-slot:[tab]>
			<p
				v-if="label && selectedVariant && hasSwatches"
				class="!p-0 !m-0 text-xs/[12px] !mb-3 font-medium tracking-wider uppercase"
			>
				{{ label }}: <span class="text-[15px]/[16px] normal-case">{{ selectedVariant.title }}</span>
			</p>
			<p v-else-if="label && hasOptions" class="!p-0 !m-0 text-xs/[12px] !mb-3 font-medium tracking-wider uppercase">
				{{ label }}
			</p>
			<ul v-if="hasSwatches" class="grid grid-cols-swatches gap-x-2 gap-y-4 justify-between">
				<li
					v-for="item of items"
					:key="item.id"
					:class="[
						'rounded-full h-9 w-9 flex items-center justify-center',
						'border-swatch border-solid border-stone-200',
						isSelectedVariant(item) && '!border-black !border-2',
					]"
				>
					<button
						type="button"
						:style="{
							backgroundImage: item.hasSwatch ? `url('${item.swatchUrl}')` : 'none',
							backgroundColor: !item.hasSwatch ? item.hexCode : '#f5f5f5',
						}"
						:class="[
							'w-7 h-7 rounded-full group shrink-0 !p-0.5 relative bg-contain bg-no-repeat bg-center',
							isSelectedVariant(item) && 'shadow-sm',
						]"
						@click="handleVariantSelection(item)"
						:aria-label="item.title"
					>
						<span
							class="group-hover:opacity-100 select-none pointer-events-none transition-opacity text-sm bg-gray-600 px-1 text-white absolute whitespace-nowrap left-1/2 -translate-x-1/2 z-10 -bottom-6 opacity-0"
						>
							{{ item.title }}
						</span>
						<span v-if="item.isNew" class="text-orange-800 absolute text-[9px] left-1/2 -translate-x-1/2 -bottom-5">
							New
						</span>
					</button>
				</li>
			</ul>
			<ul v-else-if="hasOptions" class="grid grid-cols-options gap-1.5 justify-between !mb-4">
				<li v-for="item of items" :key="item.id">
					<button
						type="button"
						:class="[
							'!px-2 h-12 w-full flex items-center justify-center',
							'border-swatch border-solid',
							isSelectedVariant(item) ? '!border !border-black bg-neutral-100 shadow-active' : 'border-zinc-400',
						]"
						@click="handleVariantSelection(item)"
						:aria-label="item.title"
					>
						{{ item.title }}
					</button>
				</li>
			</ul>
		</template>
	</headless-tabs>
	<div v-else-if="hasSwatches">
		<p
			v-if="label && selectedVariant && hasSwatches"
			class="!p-0 !m-0 text-xs/[12px] !mb-3 font-medium tracking-wider uppercase"
		>
			{{ label }}: <span class="text-[15px]/[16px] normal-case">{{ selectedVariant.title }}</span>
		</p>
		<p v-else-if="label && hasOptions" class="!p-0 !m-0 text-xs/[12px] !mb-3 font-medium tracking-wider uppercase">
			{{ label }}
		</p>
		<ul class="grid grid-cols-swatches gap-x-2 gap-y-4 justify-between !mb-4">
			<li
				v-for="item of variantItems"
				:key="item.id"
				:class="[
					'rounded-full h-9 w-9 flex items-center justify-center',
					'border-swatch border-solid border-stone-200',
					isSelectedVariant(item) && '!border-black !border-2',
				]"
			>
				<button
					type="button"
					:style="{
						backgroundImage: item.hasSwatch ? `url('${item.swatchUrl}')` : 'none',
						backgroundColor: !item.hasSwatch ? item.hexCode : '#f5f5f5',
					}"
					:class="[
						'w-7 h-7 rounded-full shrink-0 !p-0.5 group relative bg-cover',
						isSelectedVariant(item) && 'shadow-sm',
					]"
					@click="handleVariantSelection(item)"
					:aria-label="item.title"
				>
					<span
						class="group-hover:opacity-100 select-none pointer-events-none transition-opacity text-sm bg-gray-600 px-1 text-white absolute whitespace-nowrap left-1/2 z-10 -bottom-6 opacity-0"
					>
						{{ item.title }}
					</span>
					<span v-if="item.isNew" class="text-orange-800 absolute text-[9px] left-1/2 -translate-x-1/2 -bottom-5">
						New
					</span>
				</button>
			</li>
		</ul>
	</div>
	<div v-else-if="hasOptions">
		<div class="flex items-center justify-between !mb-3">
			<p v-if="label" class="!p-0 !m-0 text-xs/[12px] font-medium tracking-wider uppercase">{{ label }}</p>
			<slot name="size-guide" />
			<slot name="starter-collections" />
		</div>
		<ul class="grid grid-cols-options gap-1.5 justify-between !mb-4">
			<li v-for="item of variantItems" :key="item.id">
				<button
					type="button"
					:class="[
						'!px-2 h-12 w-full flex items-center justify-center',
						'border-swatch border-solid relative',
						isSelectedVariant(item) ? '!border !border-black bg-neutral-100 shadow-active' : 'border-zinc-400',
					]"
					@click="handleVariantSelection(item)"
					:aria-label="item.title"
				>
					<span class="whitespace-nowrap">{{ item.title }}</span>
					<span v-if="item.isNew" class="text-orange-800 absolute text-[9px] left-1/2 -translate-x-1/2 bottom-0">
						New
					</span>
				</button>
			</li>
		</ul>
	</div>
</template>
<script setup>
import { ref, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter, useRoute } from 'vue-router'

import { useProductStore } from '@stores'

const props = defineProps({
	variants: {
		type: [Object, Array],
		required: true,
	},
	selectedVariantId: {
		type: [String, Number],
		required: false,
		default: null,
	},
	label: {
		type: String,
		defualt: null,
	},
	hasSwatches: {
		type: Boolean,
		default: false,
	},
	hasOptions: {
		type: Boolean,
		default: false,
	},
	isArray: {
		type: Boolean,
		default: false,
	},
})

const store = useProductStore()
const { hasVariantCategories, variantCategories, variantItems, selectedVariant } = storeToRefs(store)
const { setVariants, setSelectedVariantId } = store
const router = useRouter()
const route = useRoute()

const defaultTab = ref(0)

const path = computed(() => route.path)
const params = computed(() => route.params)

onMounted(() => {
	if (Array.isArray(props.variants)) {
		// ** NOTE --> Hack
		const BLACKLIST = ['120128', '120129', '120130'] // 3x Silver Palettes (in Collection only)
		const applicableVariants = props.variants.filter((item) => !BLACKLIST.includes(item.sku))
		setVariants(applicableVariants)
		const blackListItems = props.variants.filter((item) => BLACKLIST.includes(item.sku))
		if (props.selectedVariantId && !blackListItems.some((item) => item.id === parseInt(props.selectedVariantId))) {
			setSelectedVariantId(parseInt(props.selectedVariantId), router, path.value, params.value)
			if (hasVariantCategories.value) {
				const index = variantCategories.value.findIndex((category) => category === selectedVariant.value.variantGroup)
				if (index >= 0) {
					defaultTab.value = index
				}
			}
		} else {
			setSelectedVariantId(applicableVariants[0].id, router, path.value, params.value)
			if (hasVariantCategories.value) {
				const index = variantCategories.value.findIndex((category) => category === selectedVariant.value.variantGroup)
				if (index >= 0) {
					defaultTab.value = index
				}
			}
		}
	} else {
		// object - nested categories (lip + cheak, etc)
		setVariants(props.variants)
		if (props.selectedVariantId) {
			setSelectedVariantId(parseInt(props.selectedVariantId), router, path.value, params.value)
			if (hasVariantCategories.value) {
				const index = variantCategories.value.findIndex((category) => category === selectedVariant.value.variantGroup)
				if (index >= 0) {
					defaultTab.value = index
				}
			}
		}
	}
})

const isSelectedVariant = (item) => item.id === selectedVariant?.value.id
const handleVariantSelection = (item) => setSelectedVariantId(item.id, router, path.value, params.value)
const handleTabChange = (tab) => {
	const firstItem = variantItems.value[tab][0]
	setSelectedVariantId(firstItem.id, router, path.value, params.value)
}
</script>
